/**
 * 공개 사이트(홈·서비스·매뉴얼·업데이트 소식·언론보도·문의) 토큰.
 *
 * 값의 정본은 확정 시안(2026-10-23~28 아티팩트)의 `site.css` `:root` 다 — 화면이 시안과 같아야 하므로
 * 앱 토큰(`COLOR_CONSTANTS`·`WEB_FONT_SIZES`)으로 바꿔 쓰지 않는다. 여백은 토스 실측 기준(구역 사이 240px@1440).
 * 화면 폭에 따라 바뀌는 값(거터·그림 모서리·구역 여백)은 CSS 변수로 두고 `SITE_CSS_VARS` 가 기본값을 정한다.
 */

export const SITE_COLOR = {
  ink: "rgba(0,0,0,.87)",
  ink2: "rgba(0,0,0,.6)",
  ink3: "rgba(0,0,0,.38)",
  line: "rgba(0,0,0,.12)",
  line2: "rgba(0,0,0,.08)",
  bg: "#FFFFFF",
  panel: "#F2F4F6",
  panel2: "#F6F7F9",
  rail: "#ECEFF1",
  grey50: "#FAFAFA",
  grey100: "#F5F5F5",
  footerBg: "#F9FAFB",
  blue: "#1D74FF",
  blueDark: "#1663DF",
  blue50: "#F0F5FF",
  /** 흰 바탕과 뚜렷이 구분되는 밝고 푸릇한 연파랑 — 매뉴얼 손님 안내 띠 */
  blue100: "#DCEBFF",
  blueAlpha: "rgba(29,116,255,.1)",
  navy: "#2751A3",
  gold: "#F0C66D",
  red: "#E53935",
  red50: "#FDDEDB",
  redDark: "#C62828",
  /** 로고 글자·제목처럼 가장 진한 글자 */
  heading: "#1C1F25",
  /** 머리말 메뉴 글자 */
  navText: "#333D4B",
  /** 글자 단추·메뉴 항목 위에 올렸을 때 바탕 */
  hoverTint: "rgba(0,0,0,.04)",
} as const;

/** 로그인·회원가입·비밀번호 변경 화면(G9-6) — 채운 입력 칸·연회색 바탕(토스식 폼) */
export const SITE_AUTH_COLOR = {
  backdrop: "linear-gradient(180deg,#F4F6F8 0%,#E8ECF0 100%)",
  description: "#6B7684",
  label: "#4E535C",
  field: "#F7F8FA",
  fieldBorder: "#D7DCE3",
  fieldBorderHover: "#C3CAD4",
  placeholder: "#8B919A",
  primaryHover: "#1462F0",
  stepBackground: "#EAF2FF",
  /** 입력 오류·확인 안내(시안에 없는 상태 — 흰 바탕에서 읽히는 대비로) */
  error: "#D93025",
  success: "#12805C",
} as const;

export const SITE_FONT_FAMILY =
  '"Pretendard",-apple-system,BlinkMacSystemFont,"Apple SD Gothic Neo","Malgun Gothic",system-ui,sans-serif';

export const SITE_SHADOW = {
  /** 흰 화면 그림이 흰 바탕에 묻히지 않게 두르는 옅은 테두리선 + 그림자 */
  frame: "0 0 0 1px rgba(15,23,42,.07),0 10px 30px rgba(28,44,66,.1)",
} as const;

export const SITE_EASE = {
  out: "cubic-bezier(.2,.75,.25,1)",
  inOut: "cubic-bezier(.65,0,.35,1)",
} as const;

/**
 * 시안 반응형 경계(max-width)의 쿼리 본문 — 스크립트의 `matchMedia` 가 이것을 쓴다. 스타일은 이것으로 만든 `SITE_MEDIA` 를 써서,
 * 배치(CSS)와 계산(스크립트)이 같은 경계에서 바뀐다.
 */
export const SITE_QUERY = {
  wide: "(max-width:1100px)",
  narrowDesktop: "(max-width:1080px)",
  tablet: "(max-width:900px)",
  compact: "(max-width:760px)",
  phone: "(max-width:640px)",
  reducedMotion: "(prefers-reduced-motion: reduce)",
  /** 마우스처럼 올려 볼 수 있는 기기 — 올렸을 때만 보이는 조작은 터치 화면에서 늘 보이게 이 안에서만 숨긴다 */
  hover: "(hover: hover) and (pointer: fine)",
  /**
   * 휴대폰·태블릿·폴더블을 가로로 둔 터치 화면 — 높이가 낮아 좁은 화면 배치의 전폭 그림·영상이 화면을 넘는다.
   * 마우스 화면(좁힌 데스크톱 창 포함)은 `pointer: coarse` 로 뺀다. 1100px 를 넘는 가로 태블릿은 데스크톱 배치로도 넉넉하다.
   * 좁은 화면 규칙(`tablet`·`compact`)과 함께 맞을 때 이 규칙이 이기도록 스타일마다 그 뒤에 둔다.
   */
  landscapeTouch:
    "(orientation: landscape) and (pointer: coarse) and (max-width:1100px)",
} as const;

/** 시안 반응형 경계의 미디어 쿼리 문자열(`SITE_QUERY` 와 같은 경계). MUI 기본 경계와 다르므로 이것으로 쓴다. */
export const SITE_MEDIA = {
  wide: `@media ${SITE_QUERY.wide}`,
  narrowDesktop: `@media ${SITE_QUERY.narrowDesktop}`,
  tablet: `@media ${SITE_QUERY.tablet}`,
  compact: `@media ${SITE_QUERY.compact}`,
  phone: `@media ${SITE_QUERY.phone}`,
  reducedMotion: `@media ${SITE_QUERY.reducedMotion}`,
  hover: `@media ${SITE_QUERY.hover}`,
  landscapeTouch: `@media ${SITE_QUERY.landscapeTouch}`,
} as const;

/** 머리말 안쪽 높이 */
export const SITE_HEADER_HEIGHT_PX = 58;
/** 머리말 아래 테두리 폭 */
export const SITE_HEADER_BORDER_PX = 1;
/** 머리말이 차지하는 전체 높이(안쪽 + 아래 테두리) — 화면 높이에서 머리말 자리를 뺄 때 쓴다 */
export const SITE_HEADER_OUTER_HEIGHT_PX =
  SITE_HEADER_HEIGHT_PX + SITE_HEADER_BORDER_PX;
/**
 * 머리말이 지금 차지하는 위쪽 높이(CSS 변수) — 보이면 머리말 전체 높이, 스크롤로 숨으면 0. 머리말 아래에 붙는 틀(홈 첫 장면)이
 * 이 값만큼 위를 잘라 두어 머리말 자리까지 걷힌다. 길이로 등록(`registerSiteHeaderTopProperty`)해 머리말이 미끄러지는 것과 같은
 * 박자로 바뀐다. 숨김은 루트의 `SITE_HEADER_HIDDEN_ATTR` 로 알린다.
 */
export const SITE_HEADER_TOP_VAR = "--site-header-top";
export const SITE_HEADER_HIDDEN_ATTR = "data-site-header-hidden";
/** 사이트 틀 기본 줄 높이 — 줄 높이를 정하지 않은 글자(예: 머리말 eyebrow)가 물려받는다 */
export const SITE_LINE_HEIGHT = 1.6;
/** 사이트 루트(html)의 scroll-padding-top — 해시·초점 이동이 머리말에 가리지 않게. 요소의 scroll-margin 은 이 값에 더해진다 */
export const SITE_SCROLL_PADDING_TOP_PX = 72;
export const SITE_CONTENT_MAX_WIDTH_PX = 1224;

/** 화면 폭에 따라 바뀌는 CSS 변수 이름 */
export const SITE_VAR = {
  gutter: "var(--gutter)",
  radiusImage: "var(--r-img)",
  section: "var(--sp-sec)",
  head: "var(--sp-head)",
  block: "var(--sp-block)",
} as const;

/** `SITE_VAR` 의 기본값과 경계별 값 — 사이트 틀 루트에 한 번 붙인다. */
export const SITE_CSS_VARS = {
  "--gutter": "24px",
  "--r-btn": "6px",
  "--r-card": "8px",
  "--r-img": "36px",
  "--sp-sec": "clamp(96px,16vw,240px)",
  "--sp-head": "clamp(48px,6.6vw,100px)",
  "--sp-block": "clamp(64px,8.4vw,128px)",
  [SITE_MEDIA.tablet]: { "--gutter": "20px" },
  [SITE_MEDIA.phone]: { "--r-img": "24px" },
} as const;

/**
 * 첫 랜딩을 뺀 사이트 페이지의 좌우 여백 — 넓은 화면과 휴대폰 사이 폭(641~1351px)에서 글·그림이 화면 가장자리에 붙지 않게
 * 폭에 따라 늘린다. 1352px 이상은 본문 최대 폭이 여백을 정해 그대로이고, 휴대폰(640px 이하)은 20px 그대로다.
 */
const SITE_SUBPAGE_GUTTER = "clamp(24px,5vw,64px)";
export const SITE_SUBPAGE_CSS_VARS = {
  "--gutter": SITE_SUBPAGE_GUTTER,
  [SITE_MEDIA.tablet]: { "--gutter": SITE_SUBPAGE_GUTTER },
  [SITE_MEDIA.phone]: { "--gutter": "20px" },
} as const;

/** 글자 단계(시안 `.eyebrow`·`.h1`~`.h3`·`.lead`·`.body`) */
export const SITE_TYPE = {
  eyebrow: {
    fontSize: "17px",
    fontWeight: 600,
    color: SITE_COLOR.blueDark,
    marginBottom: "20px",
    letterSpacing: "-.005em",
  },
  h1: {
    fontSize: "clamp(34px,4.2vw,60px)",
    lineHeight: 1.22,
    fontWeight: 700,
    letterSpacing: "-.032em",
    textWrap: "balance",
  },
  h2: {
    fontSize: "clamp(28px,3.4vw,46px)",
    lineHeight: 1.36,
    fontWeight: 700,
    letterSpacing: "-.026em",
    textWrap: "balance",
  },
  h3: {
    fontSize: "clamp(21px,1.9vw,27px)",
    lineHeight: 1.42,
    fontWeight: 700,
    letterSpacing: "-.022em",
    textWrap: "balance",
  },
  lead: {
    fontSize: "clamp(16px,1.3vw,18px)",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    maxWidth: "40em",
  },
  body: {
    fontSize: "clamp(15px,1.2vw,17px)",
    lineHeight: 1.75,
    color: SITE_COLOR.ink2,
    maxWidth: "32em",
    [SITE_MEDIA.tablet]: { maxWidth: "none" },
  },
} as const;

/** 본문 폭 상자(시안 `.wrap`) */
export const SITE_WRAP = {
  maxWidth: `calc(${SITE_CONTENT_MAX_WIDTH_PX}px + var(--gutter) * 2)`,
  marginInline: "auto",
  paddingInline: "var(--gutter)",
} as const;

/**
 * 보이는 크기는 그대로 두고 손가락으로 누르는 영역만 44×44px 까지 넓힌다(Apple HIG 44pt·WCAG 2.5.5) — 요소 가운데를 덮는 가짜 요소.
 * 요소에 `::before` 를 쓰지 않을 때만 붙인다. 이웃 대상과 겹치지 않게 간격을 함께 본다.
 */
export const SITE_TOUCH_TARGET = {
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "max(100%, 44px)",
    height: "max(100%, 44px)",
    transform: "translate(-50%,-50%)",
  },
} as const;

/** 화면에는 안 보이고 보조 기술만 읽는 글자(시안 `.sr`) */
export const SITE_VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;
